<template>
  <div class="content-container">
    <h1>Performance Inquiries</h1>
    <p>Affinity Strings provides experienced professional musicians, with a wide variety of ensemble options, for just about any occasion. All musicians affiliated with Affinty Strings are experienced professionals who will add a polished touch of class to your special occasion.</p>
    
    <ImageCarousel :image-list="performanceCarouselImages" />

    <hr>

    <div class="columns-container">
      <div class="column">
        <h2>Occasions</h2>
        <ul>
          <li>Wedding Ceremonies</li>
          <li>Anniversary Celebrations</li>
          <li>Graduation Parties</li>
          <li>Baby Showers</li>
          <li>Receptions & Banquets</li>
          <li>Worship Services</li>
          <li>Funeral/Memorial Services</li>
          <li><strong>Any Special Events</strong></li>
        </ul>
      </div>
      <div class="column">
        <h2>Ensembles</h2> 
        <ul>
          <li>String Quartet (The Classic String Ensemble)</li>
          <li>String Trio or Duet (For a great string sound on a budget)</li>
          <li>String Chamber Orchestra (Fill the hall with music for a large occasion)</li>
          <li>Trumpet with a String Quartet</li>
          <li>Harp & Violin Duet</li>
          <li>Flute with Strings</li>
        </ul>
      </div>
    </div>

    <hr>

    <h1>Music</h1>
    <p>Some clients have a clear vision for the music they would like performed at their event, while others appreciate guidance in selecting repertoire that best suits the occasion. In either case, we work closely with you to ensure the music is polished, professional, and thoughtfully tailored to your event.</p>
    <p>A string quartet, consisting of two violins, viola, and cello, is the standard chamber ensemble and offers the richest range of sound, as well as the most extensive repertoire. The music selections below are provided to help guide you in exploring styles and pieces that may be well suited to your event.</p>

    <div ref="musicSamplesEl" class="music-samples">
      <div v-for="song in musicSamples" :key="song.title + song.src" class="music-sample">
        <h3 class="music-title">{{ song.title }}</h3>
        <audio controls :src="song.src" @play="pauseOtherTracks($event)"></audio>
      </div>
    </div>

    <h1>FAQs</h1>


    <p><strong>1. How do the musicians dress?</strong></p>
    <p>The musicians dress in formal attire unless otherwise requested: men in tuxedos and women in black, or black and white.</p>
    <p><strong>2. What do you require at the venue? </strong></p>
    <p>Setup depends on the instrumentation and ensemble size.  In general, there should be an armless chair for each person in the ensemble, there needs to be enough open space to perform (for example, a quartet would need about an 8’x8′ area), and, if a room is not well lit, musicians will need to be able to use an electrical outlet to plug in lighting.</p>
    <p><strong>3. Will you play outdoors? </strong></p>
    <p>Yes, we have played many outdoor weddings in a variety of settings, however, due to the unpredictability of Michigan’s weather, we cannot guarantee outdoor performances.   It is important to plan ahead for a variety of weather possibilities. For instance: Musicians will require a shaded area (NOT under trees as they can drip sap) or temporary shelter (typically 10’x10′ or larger) if performing on a hot (85˚F+) sunny day.  Musicians will only perform in a dry protected place on a rainy day (shelter needs to be large enough to provide instrument case storage and protect from blowing rain).  Musicians will generally not be willing to expose their instruments (particularly stringed instruments) to precipitation, direct sunlight, temperatures in the performance area above 85˚F or below 65°F. Please keep in mind that our performers will cooperate and do what they can to accommodate your vision for your special occasion, but their instruments are both valuable and fragile and no musician will risk damaging their instrument.  As a general rule, if the conditions might be uncomfortable for your guests (and we mean the less tolerant ones), it is probably in a range that will damage stringed instruments.</p>
    <p><strong>4. What type of occasions will you play for?</strong></p>
    <p>We will play appropriate music for just about any occasion.  Our musicians are experienced in playing for wedding ceremonies, wedding receptions, celebrations and parties of all varieties (such as anniversaries, retirements, graduations, and birthdays), baby showers, bridal showers, sacred worship services, and we have provided music for memorial services and funerals.</p>
    <p><strong>5. Will you be at the wedding rehearsal?</strong></p>
    <p>It's not necessary for us to be at the rehearsal and it would include additional expense.  Our musicians are highly experienced in playing weddings and coordinating the music to the ceremony.  A representative from the ensemble will meet briefly with the officiant shortly before the ceremony begins to make sure everyone has the same outline and expectations.   If the ensemble is playing processional music, We will also require a pre-designated person who will give the ensemble a signal to start the processional and an ensemble member will want to meet to confirm plan with that person shortly before the ceremony.</p>

    <h2>Wedding Music Selection</h2>
    <p><strong>6. Should I have music before a wedding ceremony (prelude music)? </strong></p>
    <p>Highly recommended. Providing beautiful music for your guests as they arrive and wait for the event to begin creates a wonderful ambience. We suggest contracting your ensemble to start prelude music anywhere from 15-30 minutes before the start time of the ceremony.</p>
    <p><strong>7. How do I choose the prelude music? Am I required to select the whole program?</strong></p>
    <p>It is not necessary for you to have to choose all the prelude music. If you give us an idea of the type of ambience you wish to create using a few examples, we can use our expertise to select the remainder of the music. For example, you may want to include hymns or have only secular music, a festive upbeat atmosphere or more quiet, pensive one.</p>
    <p><strong>8. How many pieces should I choose for the processional?</strong></p>
    <p>It is common to have three separate pieces: one for the entrance of the mothers, one for the bridal party, and finally one for the entrance of the bride. This will be determined by the size of the bridal party and the venue. We will be happy to help you pick out music.</p>
    <p><strong>9. How will you know when to start the processional music? </strong></p>
    <p>We will need a signal either from a representative at the venue or an usher. We will seek out the pre-arranged person before the ceremony and confirm the plan.</p>
    <p><strong>10. Do we need to know how long the music will be in order to time our entrances to finish with the music? </strong></p>
    <p>No, you can leave that up to us.  The musicians are very skilled in ending the music at just the right time. We need only know how many people are processing to each piece of music.</p>
    <p><strong>11. Should I have music during my ceremony? </strong></p>
    <p>During a Christian  or non-denominational ceremony, there is often room for short pieces of music: after readings, during the lighting of the unity candle, presentation of roses to mothers, sign of peace, and of course communion or parts of the Mass. We recommend asking your officiant about these too, as there are certain pieces of music that may not be appropriate. During a Jewish ceremony, there is typically no music from string ensembles. Often a cantor, if present, will sing during the ceremony.</p>

    <h2>Recessional</h2>
    <p><strong>12. How many pieces do I choose for the recessional? </strong></p>
    <p>In general, there would be one piece performed during the exit of the entire wedding party, and one or two more for the exit of your guests.  The length of music necessary for a recessional can vary depending on the size of the venue and the number of guests attending.  </p>
    <p><strong>13. I am having a receiving line at the church. Can you perform for that, too?</strong></p>
    <p>Yes.</p>

    <h2>Planning</h2>
    <p><strong>14. When should we select the music? </strong></p>
    <p>Generally you should contact us before you meet with your officiant so that we may help you formulate questions to ask them about music. Often there are “dos” and “don’ts” regarding certain ceremonies. After you meet with your officiant, we can talk again and solidify the program. Our three-page printable timeline checklist has been very helpful to our clients.</p>
    <p><strong>15. There is a special piece of music I would like performed. Can you do that? </strong></p>
    <p>Yes.  Even if it is not in our repertiore, or available for string ensembles, we will be happy to write an arrangement for the quartet (there is a fee for making special music arrangements).</p>
    <p><strong>16. Do we need to meet in person before the event?</strong></p>
    <p>Not necessarily. All details can be addressed via email, phone conversations or video chats. </p>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import ImageCarousel from '@/components/ImageCarousel.vue';

const performanceCarouselImages = [
  require('@/assets/performance-carousel-quartet.png'),
  { src: require('@/assets/performance-carousel-performance.png'), fit: 'contain' },
  require('@/assets/performance-carousel-1.png'),
  require('@/assets/performance-carousel-3.png'),
  require('@/assets/performance-carousel-4.jpg'),
  require('@/assets/performance-carousel-5.jpg'),
];

const musicSamplesEl = ref(null);

function pauseOtherTracks(event) {
  const container = musicSamplesEl.value;
  if (!container) return;
  container.querySelectorAll('audio').forEach((audio) => {
    if (audio !== event.target) audio.pause();
  });
}

const songsList = [
  { title: 'A Sky Full of Stars', src: require('@/assets/audio/moms-website-songs/A_Sky_Full_of_Stars.mp3') },
  { title: 'Air on the G', src: require('@/assets/audio/moms-website-songs/bach_air.mp3') },
  { title: 'All You Need Is Love', src: require('@/assets/audio/moms-website-songs/All_You_Need_is_Love.mp3') },
  { title: 'Andante Festive', src: require('@/assets/audio/moms-website-songs/Andante_Festivo_Sibelius.mp3') },
  { title: 'At Last', src: require('@/assets/audio/moms-website-songs/At_Last_Etta_James.mp3') },
  { title: 'Begin the Beguine', src: require('@/assets/audio/moms-website-songs/Quartet_Beguine_Beginning.mp3') },
  { title: 'Best Day of My Life', src: require('@/assets/audio/moms-website-songs/Best_Day_of_my_Life.mp3') },
  { title: 'Beyond the Sea', src: require('@/assets/audio/moms-website-songs/Beyond_the_Sea.mp3') },
  { title: 'Bridal Chorus', src: require('@/assets/audio/moms-website-songs/ExtraSongs/reduced/BridalChorusWagner.mp3') },
  { title: 'Bridge Over Troubled Waters', src: require('@/assets/audio/moms-website-songs/Bridge_Over_Troubled_Waters_Paul_Simon.mp3') },
  { title: "Can't Help Falling in Love", src: require('@/assets/audio/moms-website-songs/Cant_Help_Falling_In_Love_Elvis_Presley.mp3') },
  { title: 'Canon in D', src: require('@/assets/audio/moms-website-songs/Quartet_Pachelbel_Canon.mp3') },
  { title: 'Chandlier', src: require('@/assets/audio/moms-website-songs/Chandlier_Sia.mp3') },
  { title: 'Clair de Lune', src: require('@/assets/audio/moms-website-songs/Clair_de_Lune_Debussy.mp3') },
  { title: 'Concerning Hobbits', src: require('@/assets/audio/moms-website-songs/Hobbits.mp3') },
  { title: 'Crazy Love', src: require('@/assets/audio/moms-website-songs/Crazy_Love.mp3') },
  { title: "Don't Stop Me Now", src: require('@/assets/audio/moms-website-songs/Dont_Stop_Me_Now_Queen.mp3') },
  { title: 'Flower Duet from Lakme', src: require('@/assets/audio/moms-website-songs/Flower_Duet_from_Lakme_Delibes.mp3') },
  { title: 'Golden', src: require('@/assets/audio/moms-website-songs/Golden.mp3') },
  { title: 'Golden Hour', src: require('@/assets/audio/moms-website-songs/Golden_Hour.mp3') },
  { title: 'Happy', src: require('@/assets/audio/moms-website-songs/Happy.mp3') },
  { title: 'Here, There and Everywhere', src: require('@/assets/audio/moms-website-songs/Quartet_Here_There_Everywhere.mp3') },
  { title: 'Hornpipe', src: require('@/assets/audio/moms-website-songs/Quartet_Hornpipe.mp3') },
  { title: "Isn't She Lovely", src: require('@/assets/audio/moms-website-songs/Isnt_She_Lovely.mp3') },
  { title: "Jesu, Joy of Man's Desiring", src: require('@/assets/audio/moms-website-songs/ExtraSongs/reduced/JesuJoyOfMansDesiringBach.mp3') },
  { title: 'La Vie en Rose', src: require('@/assets/audio/moms-website-songs/La_Vie_en_rose_Guglielmi.mp3') },
  { title: 'Married Life', src: require('@/assets/audio/moms-website-songs/Married_Life.mp3') },
  { title: 'Open Arms', src: require('@/assets/audio/moms-website-songs/Open_Arms_Journey.mp3') },
  { title: 'Ordinary', src: require('@/assets/audio/moms-website-songs/Ordinary_Anthony_Warren.mp3') },
  { title: 'Our Love Is Here to Stay', src: require('@/assets/audio/moms-website-songs/Our_Love_is_Here_to_Stay.mp3') },
  { title: 'Prince of Denmark March', src: require('@/assets/audio/moms-website-songs/ExtraSongs/reduced/PrinceOfDenmarksMarth-Clarke.mp3') },
  { title: 'River Flows in You', src: require('@/assets/audio/moms-website-songs/River_Flows.mp3') },
  { title: 'Serenade D957 No. 4', src: require('@/assets/audio/moms-website-songs/Serenade_D957_No_4_Schubert.mp3') },
  { title: 'Simply the Best', src: require('@/assets/audio/moms-website-songs/Simply_the_Best.mp3') },
  { title: 'Starlight', src: require('@/assets/audio/moms-website-songs/Starlight.mp3') },
  { title: 'Time to Say Good Bye', src: require('@/assets/audio/moms-website-songs/Time_to_Say_Good_Bye_Sartori.mp3') },
  { title: 'Trumpet Tune', src: require('@/assets/audio/moms-website-songs/ExtraSongs/reduced/TrumpetTune-Purcell.mp3') },
  { title: 'Wedding March', src: require('@/assets/audio/moms-website-songs/ExtraSongs/reduced/WeddingMarch-Mendeissohn.mp3') },
  { title: 'Your Song', src: require('@/assets/audio/moms-website-songs/Your_Song.mp3') },
];

const musicSamples = songsList.slice().sort((a, b) => a.title.localeCompare(b.title));
</script>

<style scoped>
.content-container {
  margin: 0 auto;
  max-width: 900px;
  width: 90%;
  padding: 1.5rem 1rem;
}

.content-container hr {
  margin: 2rem 0;
  border: none;
  border-top: 1px solid #e0e0e0;
}

.content-container > img {
  width: 100%;
  max-width: 700px;
  height: auto;
  display: block;
  margin: 1.5rem auto;
  border-radius: 4px;
}

.columns-container {
  display: flex;
  justify-content: space-between;
  gap: 40px;
}

.column {
  flex: 1;
}

.column ul {
  padding: 0;
}

.column h2 {
  margin-top: 0;
  text-align: center;
  text-decoration: underline;
}

.content-container audio {
  width: 100%;
}

.music-samples {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem 2rem;
  margin-top: 1.5rem;
}

.music-sample {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.music-title {
  margin: 0;
  font-family: "Crimson Text", serif;
  font-size: 1.1rem;
  font-weight: normal;
  text-decoration: none;
}

.music-sample audio {
  width: 100%;
  min-width: 0;
}

@media (max-width: 900px) {
  .music-samples {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Mobile Styles */
@media (max-width: 600px) {
  .content-container {
    width: 95%;
  }

  .columns-container {
    flex-direction: column;
    gap: 20px;
  }

  .column {
    text-align: center;
  }

  .column ul {
    list-style-type: none;
    padding: 0;
  }

  .music-samples {
    grid-template-columns: 1fr;
  }
}
</style>
  